import React from "react";
import {
  ActionIcon,
  Group,
  Text,
  Combobox,
  useCombobox,
  ScrollArea,
} from "@mantine/core";
import { IconWorld, IconX } from "@tabler/icons-react";
import { useState, useMemo } from "react";
import { useTimeZone } from "./useTimeZone";
import styles from "./TimeZoneSelect.module.scss";
import { MobileModalLayout } from "../ModalLayout/MobileModalLayout";
import { defaultTimeZone, TimeZonesList } from "../../utils/timeZones";

function TimeZoneSelectorBox({
  options,
  current,
  selectTimeZone,
  resetTimeZone,
}: {
  options: TimeZonesList[];
  current: TimeZonesList | undefined;
  selectTimeZone: (v: string) => void;
  resetTimeZone: () => void;
}) {
  const combobox = useCombobox();
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    return options.filter((tz) =>
      tz.city.toLowerCase().includes(query.toLowerCase())
    );
  }, [query, options]);

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(value) => {
        selectTimeZone(value);
        setQuery("");
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            minHeight: 40,
            border: "1px solid var(--mantine-color-gray-4)",
            borderRadius: 6,
            backgroundColor: "white",
            padding: "0 10px",
            position: "relative",
          }}
          onClick={() => combobox.openDropdown()}
        >
          <IconWorld className={styles.icon} />
          <input
            type="text"
            placeholder={current?.label ?? "Search timezone"}
            value={query}
            onChange={(e) => {
              setQuery(e.currentTarget.value);
              combobox.openDropdown();
            }}
            style={{
              flex: 1,
              marginLeft: 6,
              border: "none",
              outline: "none",
              fontWeight: 500,
              color: "var(--brand-color)",
              background: "transparent",
              fontSize: 14,
            }}
          />

          {current?.value !== defaultTimeZone && (
            <ActionIcon
              size="24"
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation();
                resetTimeZone();
                setQuery("");
                combobox.closeDropdown();
              }}
            >
              <IconX className={styles.icon} />
            </ActionIcon>
          )}
        </div>
      </Combobox.Target>

      <Combobox.Dropdown style={{ width: "100%" }}>
        <ScrollArea.Autosize mah={220}>
          <Combobox.Options>
            {filtered.length > 0 ? (
              filtered.map((tz) => (
                <Combobox.Option style={{fontSize: 14}} value={tz.value} key={tz.value}>
                  <Group justify="space-between">
                    <Text>{tz.label}</Text>
                  </Group>
                </Combobox.Option>
              ))
            ) : (
              <Combobox.Empty>No results found</Combobox.Empty>
            )}
          </Combobox.Options>
        </ScrollArea.Autosize>
      </Combobox.Dropdown>
    </Combobox>
  );
}

export function TimeZoneMobile() {
  const [opened, setOpened] = useState(false);
  const { options, current, selectTimeZone, resetTimeZone } = useTimeZone();

  return (
    <>
      <Group
        gap={6}
        style={{
          border: "1px solid var(--mantine-color-gray-4)",
          borderRadius: 8,
          background: "white",
          cursor: "pointer",
          padding: "4px 8px",
        }}
        onClick={() => setOpened(true)}
      >
        <IconWorld size={18} color="var(--brand-color)" />
        <Text fz={13} fw={500} style={{ color: "var(--brand-color)" }}>
          {current?.utc || "UTC"}
        </Text>
      </Group>

      {opened && (
        <div className={styles.mobileFilter}>
          <MobileModalLayout
            title="Select your timezone"
            onClose={() => setOpened(false)}
            onApply={() => setOpened(false)}
            onClear={resetTimeZone}
            applyLabel="Apply timezone"
          >
            <TimeZoneSelectorBox
              options={options}
              current={current}
              selectTimeZone={selectTimeZone}
              resetTimeZone={resetTimeZone}
            />
          </MobileModalLayout>
        </div>
      )}
    </>
  );
}
